import { Inject, Injectable } from '@angular/core';
import { BsModalService } from 'ngx-bootstrap/modal';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { BarcodeScannerModalComponent } from '../../components/barcode-scanner-modal/barcode-scanner-modal.component';
import { ConfigConstants } from '../../config/config-constants';
import { BarcodeScannerInterface } from '../../interfaces/barcode-scanner.interface';
import { ModalClass } from '../../enums/modal-bootstrap-classes.enum';
import { PaceBarcode } from '@evorait/pace-components';
import { LocalStorageCommonService } from '../local-storage/local-storage-common.service';
import {
    BARCODE_CLIPBOARD_CONCURRENT_STORAGE_KEY,
    BARCODE_CLIPBOARD_LOCAL_STORAGE_KEY,
    BARCODE_CLIPBOARD_MAX_QUEUE,
} from './barcode-scanner.const';
import { ConcurrentUsageService } from '../concurrent-usage/concurrent-usage.service';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';

@Injectable()
export class BarcodeScannerService implements BarcodeScannerInterface {
    private barcodeClipboard: BehaviorSubject<PaceBarcode[]> = new BehaviorSubject([]);
    public barcodeClipboard$: Observable<PaceBarcode[]> = this.barcodeClipboard.asObservable();
    public constructor(
        private readonly modalService: BsModalService,
        private readonly localStorageCommonService: LocalStorageCommonService,
        private readonly concurrentUsageService: ConcurrentUsageService,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {
        this.updateClipboard();
        window.addEventListener('storage', (event) => {
            if (event.key === BARCODE_CLIPBOARD_LOCAL_STORAGE_KEY) {
                this.updateClipboard();
            }
        });
    }

    private updateClipboard(): void {
        const storedValue: PaceBarcode[] | undefined = this.localStorageCommonService.storageGet(
            BARCODE_CLIPBOARD_LOCAL_STORAGE_KEY,
        );
        if (storedValue?.length) {
            this.barcodeClipboard.next(storedValue);
        } else {
            this.barcodeClipboard.next([]);
        }
    }

    public scan(
        pattern?: string,
        appId = ConfigConstants.ApplicationIdentifier.Default,
        clipboardAccessible = true,
    ): Observable<string> {
        const modalInstance = this.modalService.show(BarcodeScannerModalComponent, {
            backdrop: 'static',
            class: ModalClass.MODAL_MEDIUM,
            initialState: { pattern, appId, clipboardAccessible },
            ariaLabelledBy: 'barcode-scanner-modal-title',
        });

        return modalInstance.content?.scanCompleted.asObservable() ?? of('');
    }

    public setBarcodeClipboard(barcodes: PaceBarcode[]): void {
        if (!barcodes?.length) {
            barcodes = [];
        }
        if (barcodes.length > BARCODE_CLIPBOARD_MAX_QUEUE) {
            barcodes = barcodes.slice(0, BARCODE_CLIPBOARD_MAX_QUEUE);
        }

        this.localStorageCommonService.storageSet(BARCODE_CLIPBOARD_LOCAL_STORAGE_KEY, barcodes);
        this.updateClipboard();
    }

    public uploadBarcodeClipboard(barcodes: PaceBarcode[]): Observable<boolean> {
        const newValue = JSON.stringify(barcodes);
        return this.concurrentUsageService
            .writeDataForKey(BARCODE_CLIPBOARD_CONCURRENT_STORAGE_KEY, newValue)
            .pipe(map((data) => data.key === BARCODE_CLIPBOARD_CONCURRENT_STORAGE_KEY && data.value === newValue));
    }

    public fetchBarcodeClipboard(): Observable<boolean> {
        return this.concurrentUsageService.readDataForKey(BARCODE_CLIPBOARD_CONCURRENT_STORAGE_KEY).pipe(
            take(1),
            map((data) => {
                if (data?.length < 1) {
                    this.setBarcodeClipboard([]);
                    return true;
                }
                try {
                    const barcodeClipboard = JSON.parse(data[0].value) as PaceBarcode[];
                    this.setBarcodeClipboard(barcodeClipboard);
                    return true;
                } catch (e) {
                    this.log.error(
                        `${this.constructor.name}: was not able to parse barcode clipboard data: ${JSON.stringify(
                            data,
                        )} -- error: ${JSON.stringify(e)}`,
                    );
                    this.setBarcodeClipboard([]);
                }
                return false;
            }),
        );
    }
}
